import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { DepositApi } from 'src/app/shared/apis/deposit.api';
import { WalletApi } from 'src/app/shared/apis/wallet.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSInputComponent } from 'src/app/shared/components/cs-input/cs-input.component';
import { CSSelectComponent, ICSOption } from 'src/app/shared/components/cs-select/cs-select.component';
import { CustomizeFormModule } from 'src/app/shared/components/customize-form/customize-form.module';
import { LoadingModule } from 'src/app/shared/directive/loading/loading.module';
import { BuyCryptoMethod, CryptoOnrampParam } from 'src/app/shared/interfaces/deposit.interface';
import { ForbidDepositType, ForbidPaymentCategory } from 'src/app/shared/interfaces/wallet.interface';
import { CurrencyValuePipe } from 'src/app/shared/pipes/currency-value.pipe';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { WalletService } from '../../wallet.service';
import { DepositBonusDropdownComponent } from '../deposit-bonus-dropdown/deposit-bonus-dropdown.component';
import { DepositWithdrawForbidComponent } from '../deposit-withdraw-forbid/deposit-withdraw-forbid.component';

type CardDepositMethod = 'card' | 'applepay' | 'googlepay' | 'openbanking' | 'interac' | 'ideal';
type CardDepositAmountLimit = {
  minAmount: number;
  maxAmount: number;
};
type CardDepositCurrencyConfig = {
  PROVIDER: 'RILLPAY' | 'INSTA_X';
  FTD_CURRENCY: string[];
  STD_CURRENCY: string[];
};
enum CardDepositProviderKind {
  Unknown = 'UNKNOWN',
  InstaX = 'INSTA_X',
  Rillpay = 'RILLPAY',
}
type CardDepositPaymentMethodCode = 'CREDIT_CARD' | 'APPLE_PAY' | 'GOOGLE_PAY' | 'OPEN_BANKING' | 'INTERAC' | 'IDEAL';
type CardDepositPaymentMethodConfig = {
  FTD_METHODS: CardDepositPaymentMethodCode[];
  STD_METHODS: CardDepositPaymentMethodCode[];
  METHOD_ORDER: CardDepositPaymentMethodCode[];
  METHOD_LIMITS?: Partial<
    Record<
      CardDepositPaymentMethodCode,
      {
        MIN_EUR: number;
        MAX_EUR: number;
      }
    >
  >;
  CURRENCY_METHOD_MAP: Record<
    string,
    {
      FTD: CardDepositPaymentMethodCode[];
      STD: CardDepositPaymentMethodCode[];
    }
  >;
};

@UntilDestroy()
@Component({
  selector: 'app-deposit-crypto',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CSButtonComponent,
    CustomizeFormModule,
    PipesModule,
    DepositBonusDropdownComponent,
    CSSelectComponent,
    CSInputComponent,
    PipesModule,
    DepositWithdrawForbidComponent,
    LoadingModule,
  ],
  templateUrl: './deposit-crypto.component.html',
  styleUrl: './deposit-crypto.component.scss',
})
export class DepositCryptoComponent implements OnInit, OnDestroy {
  readonly paymentProviderKind = CardDepositProviderKind;

  constructor(
    public walletService: WalletService,
    public appService: AppService,
    private depositApi: DepositApi,
    private fb: FormBuilder,
    private userAssetsService: UserAssetsService,
    private walletApi: WalletApi,
    private currencyValuePipe: CurrencyValuePipe,
    private toastService: ToastService,
    public localStorageService: LocalStorageService,
  ) {}

  // 存款禁止加載狀態
  isForbidLoading: boolean = false;
  isDepositForbid: boolean = false;

  form: FormGroup = this.fb.group({
    payAmount: new FormControl(''),
    getAmount: new FormControl(''), // 新增：Crypto 欄位可輸入
  });

  // 狀態
  bonusLoading = false;
  rillPayLoading = false;
  activityNo = '';
  paymentProviderLoading = false;
  hasEligibleProviders = true;
  isFtdPlayer = false;
  // FTD 玩家且 checkBuyCrypto 未回傳 InstaX 管道時，顯示 Payment method unavailable 畫面
  isPaymentMethodUnavailable = false;

  /** payment method */
  currentPaymentMethod: string | null = null;
  private readonly allPaymentMethodList: ICSOption[] = [
    { value: 'card', label: 'Credit card', img: 'assets/images/deposit-crypto/credit.png' },
    {
      value: 'applepay',
      label: 'Apple Pay',
      img: 'assets/images/deposit-crypto/apple.png',
      lightImg: 'assets/images/deposit-crypto/apple-light.png',
    },
    { value: 'googlepay', label: 'Google Pay', img: 'assets/images/deposit-crypto/google.png' },
    {
      value: 'openbanking',
      label: 'Open Banking',
      img: 'assets/images/deposit-crypto/openbanking.png',
      lightImg: 'assets/images/deposit-crypto/openbanking-light.png',
    },
    { value: 'interac', label: 'Interac', img: 'assets/images/deposit-crypto/interac.png' },
    { value: 'ideal', label: 'iDEAL', img: 'assets/images/deposit-crypto/ideal.png' },
  ];
  paymentMethodList: ICSOption[] = [];

  /** pay fiat */
  currentPayFiat = 'EUR';
  payFiatList: ICSOption[] = [];
  private allPayFiatList: ICSOption[] = [];

  /** get crypto */
  currentCurrency = 'USDT';
  getFiatList: any[] = [];

  /** fiat rate（法幣 -> Crypto 的 rate 列表；找出當前 crypto 的 rate） */
  fiatRates: { currency: string; rate: number }[] = [];
  fiatRateId = '';
  /** 支付方式獲取loading */
  paymentLoading = true;
  /** 支付方式是否有打開 */
  isFaitToCryptoOpen: boolean = false;

  // API / provider list 回來的原始 provider code，送單時直接帶這個值。
  currentPaymentProvider: string | null = null;
  paymentProviderList: ICSOption[] = [];

  // 內部旗標（避免 valueChanges 互相觸發造成遞迴）
  private syncing = false;
  private setupSyncOnce = false;

  // 目前法幣與加密幣的剩餘上限（會被動態計算）
  private remainingFiatLimit: number | null = null; // 以 currentPayFiat 表示
  private remainingCryptoLimit: number | null = null; // 以 currentCurrency 表示
  private providerSupportedFiats = new Map<string, Set<string>>();
  private providerCurrencyLimits = new Map<string, Map<string, CardDepositAmountLimit>>();
  private globalSupportedFiats = new Set<string>();
  private hasProviderFiatConfig = false;
  private shouldNotifyPaymentNotSupport = false;

  @HostListener('document:keydown.enter', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.form.valid && this.isAllLoaded()) {
      this.submit();
    }
  }

  async ngOnInit(): Promise<void> {
    // 存款前先判断是否禁止存款
    await this.checkDepositForbid();
    await this.resolvePlayerGroup();

    const restored = this.walletService.preservedDepositState;

    if (restored) {
      this.currentCurrency = restored.currentCurrency;
      this.getFiatList = restored.cryptoList;
      this.walletService.clearDepositState();
    } else {
      this.getCurrencyInfo();
    }

    this.refreshPayFiatOptions();
    this.appService.currencies$.pipe(untilDestroyed(this)).subscribe(() => {
      this.refreshPayFiatOptions();
    });
    this.appService.tenantConfig$.pipe(untilDestroyed(this)).subscribe(() => {
      this.refreshPayFiatOptions();
    });

    // 檢查買幣有沒有打開
    this.paymentLoading = true;
    this.depositApi
      .checkBuyCrypto()
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.paymentLoading = false;
        const activeMethods = (res.data?.buyCryptoMethods ?? []).filter(method => method?.status);
        this.hasProviderFiatConfig = activeMethods.length > 0;
        this.providerSupportedFiats = new Map(
          activeMethods.map(method => [
            this.normalizeProviderName(method.channelId),
            new Set(this.getSupportedFiatsFromBuyCryptoMethod(method)),
          ]),
        );
        this.providerCurrencyLimits = new Map(
          activeMethods.map(method => [
            this.normalizeProviderName(method.channelId),
            new Map(
              (method.currencyInfos ?? []).map(currencyInfo => [
                String(currencyInfo.currency).toUpperCase(),
                {
                  minAmount: this.toFinite(currencyInfo.minAmount, 0),
                  maxAmount: this.toFinite(currencyInfo.maxAmount, Number.MAX_SAFE_INTEGER),
                },
              ]),
            ),
          ]),
        );
        this.globalSupportedFiats = new Set((res.data?.currency ?? []).map(currency => String(currency).toUpperCase()));

        const isBuyCryptoOpen = this.hasProviderFiatConfig || !!res.data?.status;
        if (!isBuyCryptoOpen) return;
        this.isFaitToCryptoOpen = true;

        // FTD 玩家只能使用 InstaX；若 checkBuyCrypto 的 active methods 裡沒有 InstaX 管道，
        // 則視為支付方式不可用，顯示維護畫面而非繼續載入表單。
        const hasInstaX = activeMethods.some(
          method => this.getPaymentProviderKind(method.channelId) === CardDepositProviderKind.InstaX,
        );
        if (this.isFtdPlayer && !hasInstaX) {
          this.isPaymentMethodUnavailable = true;
          return;
        }

        this.handleFetch();
      });
  }

  ngOnDestroy(): void {
    // Card deposit 相關選擇只保留在本次 modal session，銷毀時一併重置。
    this.resetCardDepositSelections();
  }

  convertToEur() {
    return this.shouldConvertCurrentFiatToEur();
  }
  fiatEurMapping: Record<string, number> = {};

  private async getFiatEurRate() {
    const exchangeCurrencies = this.allPayFiatList
      .map(option => String(option.value).toUpperCase())
      .filter(currency => currency !== 'EUR');

    if (exchangeCurrencies.length === 0) {
      this.fiatEurMapping = { EUR: 1 };
      return;
    }

    const res = await firstValueFrom(this.walletApi.getRate('EUR', exchangeCurrencies.join(',')));
    if (res?.rates) {
      const mapping = res.rates.reduce<Record<string, number>>((acc, cur) => {
        acc[cur.currency] = cur.rate;
        return acc;
      }, {});
      this.fiatEurMapping = { EUR: 1, ...mapping };
    }
  }

  /** 目前選中 crypto 的法幣 -> crypto 匯率（例如 1 USD 可換多少 USDT） */
  private getCurrentRate(): number {
    const rate = this.fiatRates.find(x => x.currency === this.currentCurrency);
    return rate?.rate ?? 0;
  }

  private toNum(v: any): number {
    const n = typeof v === 'string' ? Number(v.trim()) : Number(v);
    return isNaN(n) ? 0 : n;
  }

  /**
   * 無條件捨去工具
   *
   * @param value
   * @param decimals
   */
  private truncate(value: number, decimals: number): number {
    if (decimals <= 0) return Math.trunc(value);
    const factor = Math.pow(10, decimals);
    return Math.trunc(value * factor) / factor;
  }

  /**
   * payAmount → getAmount (法幣→Crypto)
   *
   * @param pay
   */
  private payToGet(pay: any): number {
    if (this.convertToEur()) {
      const rate = this.fiatEurMapping[this.currentPayFiat];
      pay = this.toNum(pay) * rate;
    }
    const r = this.getCurrentRate();
    const p = this.toNum(pay);
    if (r <= 0) return 0;
    const raw = p / r; // 算出 Crypto
    return this.truncate(raw, 6); // 無條件捨去到 6 位
  }

  /**
   * getAmount → payAmount (Crypto→法幣)
   *
   * @param get
   */
  private getToPay(get: any): number {
    const r = this.getCurrentRate();
    const g = this.toNum(get);
    if (r <= 0) return 0;
    let raw = g * r; // 算出法幣
    if (this.convertToEur()) {
      const rate = this.fiatEurMapping[this.currentPayFiat];
      raw /= rate;
    }
    return this.truncate(raw, 2); // 無條件捨去到 2 位
  }

  private setupTwoWaySync() {
    if (this.setupSyncOnce) return;
    this.setupSyncOnce = true;

    const payCtrl = this.form.get('payAmount')!;
    const getCtrl = this.form.get('getAmount')!;

    payCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe(v => {
      if (this.syncing) return;
      this.syncing = true;
      const next = this.payToGet(v);
      getCtrl.setValue(next, { emitEvent: false });
      this.syncing = false;
    });

    getCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe(v => {
      if (this.syncing) return;
      this.syncing = true;
      const next = this.getToPay(v);
      payCtrl.setValue(next, { emitEvent: false });
      this.syncing = false;
    });
  }

  /**
   * 重新套用兩邊的基礎驗證，並按順序拉資料更新限制
   *
   * @param root0
   * @param root0.skipProviderRefresh
   */
  handleFetch({ skipProviderRefresh = false }: { skipProviderRefresh?: boolean } = {}) {
    // 幣別可用性會隨 provider / 玩家分組改變，先同步目前可選清單。
    this.applyPayFiatOptions();

    if (!this.currentPayFiat) {
      this.currentPaymentProvider = null;
      this.paymentProviderList = [];
      this.hasEligibleProviders = false;
      this.paymentProviderLoading = false;
      this.applyPaymentMethodOptions(true);
      return;
    }

    // 基礎驗證
    const baseValidators = [
      Validators.required,
      Validators.pattern(/^(0|[1-9]\d*)(\.\d+)?$/),
      this.decimalValidator(2), // 法幣保留 2 位
    ];

    const getBaseValidators = [
      Validators.required,
      Validators.pattern(/^(0|[1-9]\d*)(\.\d+)?$/),
      this.decimalValidator(6), // Crypto 給較多小數位，可視幣種動態調整
    ];

    this.form.get('payAmount')?.setValidators(baseValidators);
    this.form.get('getAmount')?.setValidators(getBaseValidators);

    // 順序：金流限制(min/max) -> 匯率 -> 剩餘可入金
    this.refreshPaymentAmountValidators();
    this.getFiatRate(); // 更新匯率（完成後會重新推導 get 的 min/max）
    this.getFiatEurRate();
    this.getRemainingRate(); // 設定 pay 的 remaining；匯率若可用也會推導到 get
    if (!skipProviderRefresh) {
      this.getPaymentProviders();
    }
  }

  /**
   * 幣種下拉變更
   *
   * @param _val
   */
  onCurrencyChange() {
    this.refreshPaymentAmountValidators();

    // 依新幣種的匯率推導 crypto 剩餘上限
    if (this.remainingFiatLimit != null) {
      const r = this.getCurrentRate();
      this.remainingCryptoLimit = r > 0 ? this.remainingFiatLimit / r : null;
      this.refreshPaymentAmountValidators();
    }

    const payVal = this.form.get('payAmount')?.value;
    if (payVal != null && payVal !== '') {
      this.syncing = true;
      this.form.get('getAmount')?.setValue(this.payToGet(payVal), { emitEvent: false });
      this.syncing = false;
    }
  }

  /**
   * 依目前支付路徑套用輸入限制
   */
  private async refreshPaymentAmountValidators() {
    if (!this.currentPaymentProvider) {
      this.applyAmountValidators(null);
      return;
    }

    if (this.currentPaymentProviderKind === CardDepositProviderKind.InstaX) {
      const [providerLimit, methodLimit] = await Promise.all([
        this.getProviderLimitInPayFiat(),
        this.getInstaXMethodLimitInPayFiat(),
      ]);
      this.applyAmountValidators(this.intersectAmountLimits(providerLimit, methodLimit));
      return;
    }

    if (this.currentPaymentProviderKind === CardDepositProviderKind.Rillpay) {
      const limits = await this.getProviderLimitInPayFiat();
      this.applyAmountValidators(limits);
      return;
    }

    this.applyAmountValidators(null);
  }

  private applyAmountValidators(limits: { minAmount: number; maxAmount: number } | null) {
    if (!this.currentPayFiat) return;

    // ---- payAmount 驗證器 ----
    const payCtrl = this.form.controls['payAmount'];
    payCtrl.clearValidators();

    const payValidators = [Validators.required, Validators.pattern(/^(0|[1-9]\d*)(\.\d+)?$/), this.decimalValidator(2)];

    if (limits) {
      payValidators.push(Validators.min(limits.minAmount));
      payValidators.push(Validators.max(limits.maxAmount));
    }

    if (this.remainingFiatLimit != null) {
      payValidators.push(this.remainingAmountValidator(this.remainingFiatLimit, this.currentPayFiat));
    }

    payCtrl.setValidators(payValidators);
    payCtrl.updateValueAndValidity({ emitEvent: false });

    // ---- getAmount 驗證器 ----
    const getCtrl = this.form.controls['getAmount'];
    getCtrl.clearValidators();

    const getValidators = [Validators.required, Validators.pattern(/^(0|[1-9]\d*)(\.\d+)?$/), this.decimalValidator(6)];

    const rate = this.toFinite(this.getCurrentRate(), 0);
    if (rate > 0 && limits) {
      const minGet = this.payToGet(limits.minAmount);
      const maxGet = this.payToGet(limits.maxAmount);

      getValidators.push(Validators.min(minGet));
      getValidators.push(Validators.max(maxGet));
    }
    if (this.remainingCryptoLimit != null) {
      getValidators.push(Validators.max(this.remainingCryptoLimit));
    }

    getCtrl.setValidators(getValidators);
    getCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private async getProviderLimitInPayFiat(): Promise<CardDepositAmountLimit | null> {
    if (!this.currentPayFiat) {
      return null;
    }

    const limitsByCurrency = this.providerCurrencyLimits.get(this.normalizeProviderName(this.currentPaymentProvider));
    const payFiat = String(this.currentPayFiat).toUpperCase();
    const directLimit = limitsByCurrency?.get(payFiat);
    if (directLimit) {
      return directLimit;
    }

    const eurLimit = limitsByCurrency?.get('EUR');
    if (!eurLimit) {
      return null;
    }

    if (payFiat === 'EUR') {
      return eurLimit;
    }

    return this.convertEurLimitToPayFiat(eurLimit);
  }

  private async getInstaXMethodLimitInPayFiat(): Promise<CardDepositAmountLimit | null> {
    if (this.currentPaymentProviderKind !== CardDepositProviderKind.InstaX || !this.currentPaymentMethod) {
      return null;
    }

    const config = this.getCardDepositPaymentMethodConfig();
    if (!config) {
      return null;
    }

    const methodCode = this.mapMethodValueToConfiguredCode(this.currentPaymentMethod as CardDepositMethod);
    if (!methodCode) {
      return null;
    }

    const methodLimit = config.METHOD_LIMITS?.[methodCode];
    if (!methodLimit) {
      return null;
    }

    return this.convertEurLimitToPayFiat({
      minAmount: this.toFinite(methodLimit.MIN_EUR, 0),
      maxAmount: this.toFinite(methodLimit.MAX_EUR, Number.MAX_SAFE_INTEGER),
    });
  }

  private async convertEurLimitToPayFiat(eurLimit: CardDepositAmountLimit): Promise<CardDepositAmountLimit | null> {
    const payFiat = String(this.currentPayFiat ?? '').toUpperCase();
    if (!payFiat) {
      return null;
    }

    if (payFiat === 'EUR') {
      return eurLimit;
    }

    if (!this.fiatEurMapping[payFiat]) {
      await this.getFiatEurRate();
    }

    const rate = this.fiatEurMapping[payFiat];
    if (!rate) {
      return null;
    }

    return {
      minAmount: Math.ceil(this.toFinite(this.toNum(eurLimit.minAmount) / rate, 0) * 100) / 100,
      maxAmount: Math.ceil(this.toFinite(this.toNum(eurLimit.maxAmount) / rate, Number.MAX_SAFE_INTEGER) * 100) / 100,
    };
  }

  private intersectAmountLimits(
    left: CardDepositAmountLimit | null,
    right: CardDepositAmountLimit | null,
  ): CardDepositAmountLimit | null {
    if (left && right) {
      return {
        minAmount: Math.max(left.minAmount, right.minAmount),
        maxAmount: Math.min(left.maxAmount, right.maxAmount),
      };
    }

    return left ?? right;
  }

  private toFinite(n: any, fallback: number): number {
    const v = Number(n);
    return Number.isFinite(v) ? v : fallback;
  }

  // 將原始 provider code 映射成前端判斷用的 enum，避免到處直接比對字串。
  get currentPaymentProviderKind(): CardDepositProviderKind {
    return this.getPaymentProviderKind(this.currentPaymentProvider);
  }

  get isCardDepositStateLoading(): boolean {
    return this.paymentLoading || (this.isFaitToCryptoOpen && this.paymentProviderLoading);
  }

  get hasAvailableCardDepositCurrencies(): boolean {
    return this.payFiatList.length > 0 || this.allPayFiatList.length > 0;
  }

  onPayFiatChange() {
    // 切換法幣後若舊 method 已不合法，先清掉再重新抓相依資料。
    this.shouldNotifyPaymentNotSupport = true;
    this.applyPaymentMethodOptions(true);
    this.handleFetch();
  }

  onPaymentProviderChange() {
    this.refreshPayFiatOptions();
    this.applyPaymentMethodOptions(true);
    this.handleFetch({ skipProviderRefresh: true });
  }

  onPaymentMethodChange() {
    this.handleFetch({ skipProviderRefresh: true });
  }

  private applyPaymentMethodOptions(clearInvalidSelection: boolean) {
    if (this.currentPaymentProviderKind !== CardDepositProviderKind.InstaX) {
      this.paymentMethodList = [];
      this.currentPaymentMethod = null;
      return;
    }

    const methodOrder = this.getConfiguredInstaMethodOrder();
    const allowedMethods = new Set(this.getAllowedInstaMethodsByFiat());
    this.paymentMethodList = this.allPaymentMethodList
      .filter(option => allowedMethods.has(option.value as CardDepositMethod))
      .sort(
        (left, right) =>
          methodOrder.indexOf(left.value as CardDepositMethod) - methodOrder.indexOf(right.value as CardDepositMethod),
      );

    const hasValidSelection = this.paymentMethodList.some(option => option.value === this.currentPaymentMethod);
    if (!hasValidSelection && clearInvalidSelection) {
      this.currentPaymentMethod = this.paymentMethodList[0]?.value ?? null;
      return;
    }

    if (!this.currentPaymentMethod && this.paymentMethodList.length > 0) {
      this.currentPaymentMethod = this.paymentMethodList[0].value;
    }
  }

  private getPaymentProviders() {
    if (!this.currentPayFiat) {
      this.currentPaymentProvider = null;
      this.paymentProviderList = [];
      this.hasEligibleProviders = false;
      this.paymentProviderLoading = false;
      this.applyPaymentMethodOptions(true);
      return;
    }

    const options = this.getConfiguredProviderOptionsByFiat(this.currentPayFiat);
    this.paymentProviderList = options;
    this.hasEligibleProviders = options.length > 0;
    this.paymentProviderLoading = false;

    if (!this.hasEligibleProviders) {
      if (this.shouldNotifyPaymentNotSupport) {
        this.toastService.show({
          message: 'payment not support',
          type: 'fail',
          title: '',
        });
      }
      this.currentPaymentProvider = null;
      this.shouldNotifyPaymentNotSupport = false;
      this.applyPaymentMethodOptions(true);
      return;
    }

    const previousProvider = this.currentPaymentProvider;
    this.currentPaymentProvider = options.some(option => option.value === this.currentPaymentProvider)
      ? this.currentPaymentProvider
      : (options[0]?.value ?? null);
    const providerChanged = previousProvider !== this.currentPaymentProvider;
    this.shouldNotifyPaymentNotSupport = false;

    const fiatChanged = this.refreshPayFiatOptions();
    this.applyPaymentMethodOptions(providerChanged || fiatChanged);

    if (providerChanged || fiatChanged) {
      this.handleFetch({ skipProviderRefresh: true });
    }
  }

  private applyPayFiatOptions(): boolean {
    const nextOptions = this.filterPayFiatOptionsForCurrentProvider();
    this.payFiatList = nextOptions;

    if (nextOptions.some(option => option.value === this.currentPayFiat)) {
      return false;
    }

    this.currentPayFiat = (nextOptions[0]?.value as string) || '';
    return true;
  }

  private filterPayFiatOptionsForCurrentProvider(): ICSOption[] {
    return [...this.allPayFiatList];
  }

  private getConfiguredProviderOptionsByFiat(fiat: string): ICSOption[] {
    const targetFiat = String(fiat ?? '').toUpperCase();
    const seenProviders = new Set<string>();

    return (
      this.getCardDepositCurrencyConfig()
        .filter(config => !this.shouldHideProvider(this.getCardDepositProviderKey(config)))
        // 以 checkBuyCrypto 回傳的 active methods 為準，過濾掉未實際啟用的 provider。
        // hasProviderFiatConfig 為 false（API 沒回 active methods）時略過此過濾，維持舊行為。
        .filter(
          config =>
            !this.hasProviderFiatConfig ||
            this.providerSupportedFiats.has(this.normalizeProviderName(this.getCardDepositProviderKey(config))),
        )
        .filter(config => this.getConfiguredCurrenciesByGroup(config).includes(targetFiat))
        .filter(config => {
          const providerKey = this.normalizeProviderName(this.getCardDepositProviderKey(config));
          if (!providerKey || seenProviders.has(providerKey)) {
            return false;
          }
          seenProviders.add(providerKey);
          return true;
        })
        .map(config => this.mapPaymentProviderOption(this.getCardDepositProviderKey(config)))
        .sort(
          // 當多個 provider 同時可用時，前端固定把 InstaX 排在最前面。
          (left, right) =>
            Number(this.isInstaProvider(right.value as string)) - Number(this.isInstaProvider(left.value as string)),
        )
    );
  }

  private mapPaymentProviderOption(providerCode: string): ICSOption {
    const providerKind = this.getPaymentProviderKind(providerCode);

    return {
      value: providerCode,
      label: this.getPaymentProviderLabel(providerKind, providerCode),
      img:
        providerKind === CardDepositProviderKind.InstaX
          ? 'assets/images/deposit-crypto/instaexchange.png'
          : providerKind === CardDepositProviderKind.Rillpay
            ? 'assets/images/deposit-crypto/rillpay.png'
            : undefined,
    };
  }

  private getPaymentProviderLabel(providerKind: CardDepositProviderKind, providerCode: string): string {
    if (providerKind === CardDepositProviderKind.InstaX) {
      return 'InstaX';
    }

    if (providerKind === CardDepositProviderKind.Rillpay) {
      return 'RillPay';
    }

    return providerCode;
  }

  private isInstaProvider(provider: string | null): boolean {
    return this.getPaymentProviderKind(provider) === CardDepositProviderKind.InstaX;
  }

  private isRillProvider(provider: string | null): boolean {
    return this.getPaymentProviderKind(provider) === CardDepositProviderKind.Rillpay;
  }

  private shouldHideProvider(provider: string | null): boolean {
    return this.isFtdPlayer && this.isRillProvider(provider);
  }

  private getAllowedInstaMethodsByFiat(): CardDepositMethod[] {
    const config = this.getCardDepositPaymentMethodConfig();
    if (!config) {
      return [];
    }
    const currencyConfig = config.CURRENCY_METHOD_MAP?.[this.currentPayFiat];
    const configuredMethods = this.isFtdPlayer ? currencyConfig?.FTD : currencyConfig?.STD;
    return configuredMethods
      .map(method => this.mapConfiguredMethodCode(method))
      .filter((method): method is CardDepositMethod => !!method);
  }

  private async resolvePlayerGroup() {
    const userInfo = this.appService.userInfo$.getValue() ?? this.localStorageService.localUserInfo;

    try {
      // 前端依需求文件判斷：FTD = 沒有歷史存款且沒有 MA tag。
      const hasDeposit = await this.walletService.ensureDepositStatus();
      this.isFtdPlayer = !hasDeposit && !userInfo?.isMA;
    } catch {
      this.isFtdPlayer = !userInfo?.isMA;
    }
  }

  private resetCardDepositSelections() {
    // modal 內容被銷毀時，重置 provider / method / fiat 的 session 狀態。
    this.currentPaymentProvider = null;
    this.currentPaymentMethod = null;
    this.paymentProviderList = [];
    this.paymentMethodList = [];
    this.payFiatList = [...this.allPayFiatList];
    this.shouldNotifyPaymentNotSupport = false;
  }

  private getSupportedFiatsFromBuyCryptoMethod(method: BuyCryptoMethod): string[] {
    const currencies =
      method.currencyInfos && method.currencyInfos.length > 0
        ? method.currencyInfos.map(currencyInfo => currencyInfo.currency)
        : (method.currency ?? []);

    return currencies
      .filter(currency => currency)
      .map(currency => String(currency).toUpperCase())
      .filter(Boolean);
  }

  private normalizeProviderName(value: string | null | undefined): string {
    return (value ?? '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  // 將 API / config 中可能不同格式的 provider 字串收斂成固定 enum。
  private getPaymentProviderKind(provider: string | null | undefined): CardDepositProviderKind {
    const normalizedProvider = this.normalizeProviderName(provider);

    if (normalizedProvider.includes('insta')) {
      return CardDepositProviderKind.InstaX;
    }

    if (normalizedProvider.includes('rill')) {
      return CardDepositProviderKind.Rillpay;
    }

    return CardDepositProviderKind.Unknown;
  }

  private getOnramperSubmitValue(provider: string | null | undefined): string | undefined {
    const providerKind = this.getPaymentProviderKind(provider);

    if (providerKind === CardDepositProviderKind.InstaX) {
      return 'instax';
    }

    if (providerKind === CardDepositProviderKind.Rillpay) {
      return 'rillpay';
    }

    return provider ?? undefined;
  }

  private refreshPayFiatOptions(): boolean {
    const currencyMap = new Map(
      this.appService.currencies$.getValue().map(currency => [currency.currency.toUpperCase(), currency]),
    );

    this.allPayFiatList = this.getConfiguredPayFiatCurrencies(this.currentPaymentProvider).map(currencyCode => {
      const currency = currencyMap.get(currencyCode);

      return {
        value: currencyCode,
        label: currencyCode,
        img: currency?.icon || this.getFallbackFiatIcon(currencyCode),
      };
    });

    return this.applyPayFiatOptions();
  }

  private getConfiguredPayFiatCurrencies(provider: string | null): string[] {
    const configs = this.getCardDepositCurrencyConfig().filter(
      config => !this.shouldHideProvider(this.getCardDepositProviderKey(config)),
    );
    if (configs.length === 0) {
      return [];
    }
    if (!provider) {
      return Array.from(new Set(configs.flatMap(config => this.getConfiguredCurrenciesByGroup(config))));
    }

    const providerConfig = configs.find(
      config =>
        this.normalizeProviderName(this.getCardDepositProviderKey(config)) === this.normalizeProviderName(provider),
    );

    return providerConfig ? this.getConfiguredCurrenciesByGroup(providerConfig) : [];
  }

  private getCardDepositCurrencyConfig(): CardDepositCurrencyConfig[] {
    const raw = this.appService.tenantConfig$.value?.config?.['card_deposit_currency'] as unknown;
    if (!raw) {
      return [];
    }

    if (typeof raw !== 'string') {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as CardDepositCurrencyConfig[]) : [];
    } catch {
      return [];
    }
  }

  private getCardDepositPaymentMethodConfig(): CardDepositPaymentMethodConfig | null {
    const raw = this.appService.tenantConfig$.value?.config?.['card_deposit_payment_method'] as unknown;
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      return parsed as CardDepositPaymentMethodConfig;
    } catch {
      return null;
    }
  }

  private getConfiguredCurrenciesByGroup(config: CardDepositCurrencyConfig): string[] {
    const currencies = this.isFtdPlayer ? config.FTD_CURRENCY : config.STD_CURRENCY;
    return currencies.map(currency => String(currency).toUpperCase());
  }

  private getCardDepositProviderKey(config: CardDepositCurrencyConfig): string {
    return config.PROVIDER;
  }

  private getFallbackFiatIcon(currencyCode: string): string | undefined {
    const fallbackIcons = new Set(['AUD', 'CAD', 'CNY', 'EUR', 'GBP', 'JPY', 'NZD', 'THB', 'USD', 'VND']);
    return fallbackIcons.has(currencyCode) ? `assets/images/crypto/${currencyCode}.svg` : undefined;
  }

  private getConfiguredInstaMethodOrder(): CardDepositMethod[] {
    const config = this.getCardDepositPaymentMethodConfig();
    const fallbackOrder: CardDepositMethod[] = ['card', 'applepay', 'googlepay', 'openbanking', 'interac', 'ideal'];

    if (!config) {
      return fallbackOrder;
    }

    const mappedOrder = config.METHOD_ORDER.map(method => this.mapConfiguredMethodCode(method)).filter(
      (method): method is CardDepositMethod => !!method,
    );

    return mappedOrder.length > 0 ? mappedOrder : fallbackOrder;
  }

  private mapConfiguredMethodCode(method: CardDepositPaymentMethodCode): CardDepositMethod | null {
    const methodMap: Record<CardDepositPaymentMethodCode, CardDepositMethod> = {
      CREDIT_CARD: 'card',
      APPLE_PAY: 'applepay',
      GOOGLE_PAY: 'googlepay',
      OPEN_BANKING: 'openbanking',
      INTERAC: 'interac',
      IDEAL: 'ideal',
    };

    return methodMap[method] ?? null;
  }

  private mapMethodValueToConfiguredCode(method: CardDepositMethod): CardDepositPaymentMethodCode | null {
    const methodMap: Record<CardDepositMethod, CardDepositPaymentMethodCode> = {
      card: 'CREDIT_CARD',
      applepay: 'APPLE_PAY',
      googlepay: 'GOOGLE_PAY',
      openbanking: 'OPEN_BANKING',
      interac: 'INTERAC',
      ideal: 'IDEAL',
    };

    return methodMap[method] ?? null;
  }

  private shouldConvertCurrentFiatToEur(): boolean {
    const fiat = String(this.currentPayFiat ?? '').toUpperCase();
    if (!fiat || fiat === 'EUR') {
      return false;
    }

    if (this.hasProviderFiatConfig) {
      const supportedFiats = this.providerSupportedFiats.get(this.normalizeProviderName(this.currentPaymentProvider));
      return !supportedFiats?.has(fiat);
    }

    if (this.globalSupportedFiats.size > 0) {
      return !this.globalSupportedFiats.has(fiat);
    }

    return false;
  }

  /** 取得匯率（法幣 -> Crypto） */
  getFiatRate(): void {
    this.rillPayLoading = true;
    this.depositApi
      .getFiatToVirtualRate(this.convertToEur() ? 'EUR' : this.currentPayFiat, 'Deposit')
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.fiatRates = res?.rates || [];
        this.fiatRateId = res?.rateId || '';
        this.rillPayLoading = false;

        // 依新匯率更新 crypto 剩餘上限
        if (this.remainingFiatLimit != null) {
          const r = this.getCurrentRate();
          this.remainingCryptoLimit = r > 0 ? this.remainingFiatLimit / r : null;
        }

        // 重新套用所有驗證（含剩餘額度）
        this.refreshPaymentAmountValidators();

        // 建立雙向同步（僅一次）
        this.setupTwoWaySync();

        // 用最新匯率同步顯示
        const payVal = this.form.get('payAmount')?.value;
        if (payVal != null && payVal !== '') {
          this.syncing = true;
          this.form.get('getAmount')?.setValue(this.payToGet(payVal), { emitEvent: false });
          this.syncing = false;
        }
      });
  }

  /** 取得剩餘可入金限制，並推導到 get 欄位 */
  async getRemainingRate() {
    if (this.userAssetsService.dpRemainingAmount$.value !== null) {
      const rateData = await firstValueFrom(this.walletApi.getRate(this.currentPayFiat, 'EUR'));

      // 將「每日剩餘額度（平台側幣別）」換算成目前法幣
      const remainingFiat = this.userAssetsService.dpRemainingAmount$.value * (rateData?.rates?.[0].rate || 0);
      this.remainingFiatLimit = remainingFiat;

      // 依現行匯率推導 crypto 的剩餘上限
      const rate = this.getCurrentRate();
      this.remainingCryptoLimit = rate > 0 ? remainingFiat / rate : null;

      // 重新套用驗證（會把剩餘額度驗證一起加回去）
      this.refreshPaymentAmountValidators();
    }
  }

  /** 取得可買幣種資訊 */
  getCurrencyInfo() {
    this.rillPayLoading = true;
    this.walletService
      .getCryptoList()
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        this.rillPayLoading = false;
        this.getFiatList = result.cryptoList;
        this.currentCurrency = this.walletService.targetCurrency || result.currentCurrency;
      });
  }

  isAllLoaded(): boolean {
    return !this.bonusLoading && !this.rillPayLoading && !this.paymentLoading && !this.paymentProviderLoading;
  }

  canSubmit(): boolean {
    return (
      this.form.valid &&
      !!this.currentPaymentProvider &&
      (this.currentPaymentProviderKind !== CardDepositProviderKind.InstaX || !!this.currentPaymentMethod)
    );
  }

  async submit() {
    if (!this.canSubmit()) return;

    this.walletService.pushInitiateDepositOnce({
      type: 'buy-crypto',
      currency: this.currentCurrency,
      amount: this.form.get('getAmount')?.value,
    });

    const params: CryptoOnrampParam = {
      amount: this.convertToEur()
        ? this.truncate(this.toNum(this.form.get('payAmount')!.value) * this.fiatEurMapping[this.currentPayFiat], 2)
        : this.truncate(this.toNum(this.form.get('payAmount')!.value), 2), // API 以法幣金額為準
      currency: this.convertToEur() ? 'EUR' : this.currentPayFiat,
      token: this.currentCurrency,
      network: 'ERC20',
      rateId: this.fiatRateId,
      activityNo: this.activityNo || 'unknowtmpcode',
      actionType: 1,
      paymentCode: 'FiatToCrypto',
      onramper: this.getOnramperSubmitValue(this.currentPaymentProvider),
      paymentType:
        this.currentPaymentProviderKind === CardDepositProviderKind.InstaX
          ? (this.currentPaymentMethod ?? undefined)
          : undefined,
      redirectUrl: `${window.location.origin}/${this.appService.languageCode}?dialog=deposit-result`,
    };

    this.rillPayLoading = true;
    this.depositApi
      .cryptoOnramp(params)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: response => {
          if (response?.success) {
            window.location.href = response.data.checkoutUrl;
          } else {
            this.toastService.show({
              type: 'fail',
              message: 'Failed to initiate crypto onramp. Please try again later.',
            });
          }
          this.rillPayLoading = false;
        },
        error: err => {
          console.error('Error during crypto onramp:', err);
          this.rillPayLoading = false;
          this.toastService.show({
            type: 'fail',
            message: 'Failed to initiate crypto onramp. Please try again later.',
          });
        },
      });
  }

  /**
   * 驗證：小數點最多 maxDecimals 位
   *
   * @param maxDecimals
   */
  private decimalValidator(maxDecimals: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value == null || control.value === '') return null;
      const value = String(control.value);
      if (!/^\d*\.?\d*$/.test(value)) return null; // 只做格式檢查；非法交給 pattern
      const parts = value.split('.');
      const decimalPart = parts[1];
      if (decimalPart && decimalPart.length > maxDecimals) {
        return { decimals: { maxDecimals } };
      }
      return null;
    };
  }

  /**
   * 驗證：剩餘可入金額
   *
   * @param amount
   * @param currency
   */
  private remainingAmountValidator(amount: number, currency: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value == null || control.value === '') return null;
      const value = Number(control.value);
      if (isNaN(value)) return null;
      return value > amount ? { remainingAmount: { amount, currency } } : null;
    };
  }

  // ==== 錯誤訊息（拆成兩組） ====
  get getPayError(): string {
    const ctrl = this.form.get('payAmount');
    const hasValue = ctrl?.value !== null && ctrl?.value !== undefined && ctrl?.value !== '';
    if (!ctrl || ctrl.valid || !(ctrl.dirty || ctrl.touched || hasValue)) return '';
    const errors = ctrl.errors ?? {};
    if (errors['required']) return 'This field is required';
    if (errors['min']) return `Amount must be at least ${errors['min'].min} ${this.currentPayFiat}`;
    if (errors['max']) return `Amount must not exceed ${errors['max'].max} ${this.currentPayFiat}`;
    if (errors['pattern']) return 'Please enter a valid number';
    if (errors['remainingAmount']) {
      const amt = this.currencyValuePipe.transform(
        errors['remainingAmount'].amount,
        errors['remainingAmount'].currency,
      );
      return `Your remaining deposit limit is ${amt} ${errors['remainingAmount'].currency}`;
    }
    if (errors['decimals']) return `Up to ${errors['decimals'].maxDecimals} decimal places allowed`;
    return 'Invalid input';
  }

  /**
   * 存款前先判断是否禁止存款
   */
  async checkDepositForbid() {
    this.isForbidLoading = true;
    this.isDepositForbid = await firstValueFrom(
      this.walletApi.forbidPaymentInfo({
        category: ForbidPaymentCategory.Deposit,
        depositType: ForbidDepositType.Legal,
      }),
    );
    this.isForbidLoading = false;
  }
}
